import React from 'react';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { textCenter, doughnutLegendPlugin, umeArcPlugin } from '/src/utilities/chart-plugins';
import { formatPercent, colors } from '/src/utilities/formats';

ChartJS.register(ArcElement, Legend, Tooltip);

const DoughnutChart = ({
    data = null,
    datasets = null,
    secondaryData = null,
    centerNumber = false,
    centerText = false,
    legendDiv = false,
    numberFormat = null,
    colorRamp = null
}) => {

    if (!data && !datasets) return null;


    // create an object mapping the labels to the values for doughnutLegendPlugin
    dataValueMap = {};
    if (data) {

        // calculate total for percentage display on Award Level Focus
        let total = data.reduce((acc, dataPoint) => {
            return acc + parseFloat(dataPoint.value);
        }, 0);

        data.forEach(dataPoint => {
            if (numberFormat != 'number') {
                dataValueMap[dataPoint.label] = formatPercent(dataPoint.value, 1);
            }
            else {
                dataValueMap[dataPoint.label] = `${parseInt(dataPoint.value).toLocaleString()} (${(dataPoint.value / total * 100).toFixed(1)}%)`;
            }
        });
    }
    if (datasets) {
        datasets.datasets[0].data.forEach((dataPoint, index) => {
            dataValueMap[datasets.labels[index]] = formatPercent(dataPoint, 1);
        });
    }

    // specify a default color ramp if one is not passed in. 
    if (!colorRamp) {
        colorRamp = [
            colors.lightblue, // light blue
            colors.red, // red
            colors.green, // green
            colors.orange, // orange
            colors.darkred, // dark red
            colors.darkblue, // dark blue
            colors.pink, // pink
        ]
    }

    const options = {
        hover: { mode: null },
        animation: false,
        responsive: true,
        maintainAspectRatio: false,
        cutout: '75%',
        plugins: {
            textCenter: {
                centerNumber: centerNumber,
                centerText: centerText
            },
            doughnutLegend: {
                // ID of the container to put the legend in
                containerID: legendDiv,
                labelsToValues: dataValueMap,
                appendURM: secondaryData ?? false,
            },
            legend: {
                display: false,
                position: 'right',
                maxWidth: 250,
                itemWrap: true,
                labels: {
                    font: {
                        family: '"Open Sans", sans-serif',
                        fontSize: 14,
                    },
                    color: '#000',
                    boxWidth: 10,
                    boxHeight: 10,
                    usePointStyle: true
                },
            }, // end legend
            // format tooltip numbers
            tooltip: {
                enabled: false,
                external: function (context) {
                    // Tooltip Element
                    let tooltipEl = document.getElementById('chartjs-tooltip');

                    // Create element on first render
                    if (!tooltipEl) {
                        tooltipEl = document.createElement('div');
                        tooltipEl.id = 'chartjs-tooltip';
                        tooltipEl.classList.add('tooltip');
                        tooltipEl.classList.add('doughnut-tooltip');
                        tooltipEl.innerHTML = '<div class="tooltip-body"></div>';
                        document.body.appendChild(tooltipEl);
                    }

                    // Hide if no tooltip
                    const tooltipModel = context.tooltip;
                    if (tooltipModel.opacity === 0) {
                        tooltipEl.style.opacity = 0;
                        return;
                    }

                    function getBody(bodyItem) {
                        return bodyItem.lines;
                    }

                    // Set Text
                    if (tooltipModel.body) {
                        const titleLines = tooltipModel.title || [];
                        const bodyLines = tooltipModel.body.map(getBody);
                        let innerHtml = '<div class="tooltip-title">';
                        titleLines.forEach(function (title) {
                            innerHtml += title;
                        });
                        innerHtml += '</div>';

                        bodyLines.forEach(function (body, i) {
                            const colors = tooltipModel.labelColors[i];
                            const div = `
                            <div class="tooltip-entry">
                                <div class="tooltip-square" style="background: ${colors.backgroundColor}; border-color: ${colors.borderColor};"></div> 
                                <div>${body}</div>
                            </div>`;
                            innerHtml += div;
                        });
                        let ttbody = tooltipEl.querySelector('div.tooltip-body');
                        ttbody.innerHTML = innerHtml;
                    }
                    const position = context.chart.canvas.getBoundingClientRect();
                    tooltipEl.style.opacity = 1;
                    tooltipEl.style.left = position.left + window.pageXOffset + tooltipModel.caretX + 'px';
                    tooltipEl.style.top = position.top + window.pageYOffset + tooltipModel.caretY + 'px';
                },
                callbacks: {
                    label: function (tooltipItem) {
                        // check if tooltipItem is defined and has a raw value
                        // formatting may already have been done in parent component
                        if (typeof (tooltipItem) != 'undefined' && typeof (tooltipItem.raw) != 'undefined') {

                            let floatValue = 0;
                            // get the float value from the tooltipItem
                            if (typeof (tooltipItem.raw) == 'number') {
                                floatValue = tooltipItem.raw;
                            } else if (typeof (tooltipItem.raw.value) == 'number') {
                                floatValue = tooltipItem.raw.value;
                            } else {
                                floatValue = parseFloat(tooltipItem.raw.value);
                            }

                            let decimalPlaces = 1;
                            let format = null;
                            // if numberFormat is an array use the first element as the format and the second as the decimal places
                            if (Array.isArray(numberFormat)) {
                                format = numberFormat[0];
                                decimalPlaces = numberFormat[1];
                            } else {
                                format = numberFormat;
                                decimalPlaces = 1;
                            }

                            let strValue = floatValue.toLocaleString();
                            switch (format) {
                                case 'currency':
                                    return ` ${strValue} USD`;
                                case 'percent':
                                    if (floatValue == 0 || floatValue == 100) {
                                        return ` ${floatValue}% `;
                                    }
                                    return ` ${floatValue.toFixed(decimalPlaces)}% `;
                                default:
                                    return ` ${strValue} `;
                            }
                        }
                    },
                },
            },

        },
    }

    const plugins = [textCenter, doughnutLegendPlugin];
    if (secondaryData) {
        options.plugins.umeArc = {
            secondaryData: secondaryData.value,
        };
        plugins.push(umeArcPlugin);
    }

    let formattedData;
    if (datasets) {
        formattedData = datasets;
    }
    else {
        formattedData = {
            labels: data.map(dataPoint => dataPoint.label),
            datasets: [{
                data: data,
                radius: '95%',
                weight: 1,
                backgroundColor: colorRamp,
                borderColor: [
                    '#FFFFFF',
                    '#FFFFFF',
                    '#FFFFFF',
                    '#FFFFFF',
                    '#FFFFFF',
                    '#FFFFFF',
                ],
                borderWidth: 2,
            }],
        };
    }
    return (
        <div>
            <div className="doughnut-chart" style={{ height: "300px" }}>
                <Doughnut data={formattedData} options={options} plugins={plugins} />
            </div>
        </div>
    )
        ;
};


export default DoughnutChart;

